import { useState, useRef, useEffect } from 'react';

import { AgGridReact } from 'ag-grid-react'; // AG Grid Component
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the grid
import "ag-grid-enterprise";
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';

import { currencyFormatter, dateFormatter, numberFormatter, durationFormatter } from '../valueformatter';

export default function SalesProductivity(props) {
    const gridOptions = {
        sideBar: true,
        rowGroupPanelShow: 'always',
        // groupDefaultExpanded: -1,     // expand all groups by default
        groupIncludeTotalFooter: true,
        suppressAggFuncInHeader: true,
        columnHoverHighlight: true,
        autoGroupColumnDef: {
            headerName: 'Group',
            field: "team_name",
            cellRendererParams: {
                suppressCount: false,
                // footerValueGetter: params => {
                //     const isRootLevel = params.node.level === -1;
                //     if (isRootLevel) {
                //         return 'Grand Total';
                //     }
                //     return `Subtotal (${params.value})`;
                // }
            }
        },
        getRowStyle: params => {
            if (params.node.group && params.node.level === -1) {
                return { backgroundColor: '#3F91B8', color: 'white', fontWeight: 'bold' };
            }
            if (params.node.group) {
                return { backgroundColor: '#b5c4ca', fontWeight: 'bold' };
            }
            return { backgroundColor: params.node.rowIndex % 2 === 0 ? '#f9f9f9' : '#c3e6f7' };
        },
        autoSizeStrategy: {
            type: 'fitCellContents',
        },
    };

    const gridRef = useRef(); // Optional - for accessing Grid's API

    // DefaultColDef sets props common to all Columns
    const defaultColDef = {
        filter: true,
        enableRowGroup: true,
    };

    // Row Agg Func
    const getGroupRowAgg = (params) => {
        const result = {
            initial_amount: 0,
            answered_count: 0,
            deal_count: 0,
            total_duration: 0,
        };

        params.nodes.forEach(node => {
            if (node.group) {
                // node is a group, use the aggregated data
                result.initial_amount += node.aggData?.initial_amount || 0;
                result.answered_count += node.aggData?.answered_count || 0;
                result.deal_count += node.aggData?.deal_count || 0;
                result.total_duration += node.aggData?.avg_duration
                    ? node.aggData.avg_duration * node.aggData.answered_count
                    : 0;
            } else if (node.data) {
                // leaf row
                result.initial_amount += node.data.initial_amount || 0;
                result.answered_count += node.data.answered_count || 0;
                result.deal_count += node.data.deal_count || 0;
                if (node.data.avg_duration && node.data.answered_count) {
                    result.total_duration += node.data.avg_duration * node.data.answered_count;
                }
            }
        });

        result.avg_initial = result.deal_count > 0
            ? result.initial_amount / result.deal_count
            : 0;

        result.calls_to_deals = result.deal_count > 0
            ? (result.answered_count / result.deal_count).toFixed(2)
            : 0;

        result.avg_duration = result.answered_count > 0
            ? (result.total_duration / result.answered_count).toFixed(0)
            : 0;

        return result;
    };

    // Column Definitions: Defines the columns to be displayed.
    const colDefs = [
        { field: "team_name", headerName: "Team", rowGroup: true, hide: true, id: 'team_name' },
        { field: "call_date", headerName: "Date", rowGroup: true, hide: true, valueFormatter: params => dateFormatter(params.value) },
        { field: "username", headerName: "Agent", id: 'username' },
        { 
            field: "initial_amount", 
            headerName: "Initial Amount", 
            aggFunc: "sum", 
            valueFormatter: params => currencyFormatter(params.value, "$"),
            cellStyle: { textAlign: 'right' } 
        },

        {
            field: "avg_initial",
            headerName: "Avg Initial",
            valueGetter: (params) =>
                params.data ? params.data.avg_initial : undefined,
            valueFormatter: params => currencyFormatter(params.value, "$"),
            cellStyle: { textAlign: 'right' } 
        },
        { 
            field: "answered_count", 
            headerName: "Answered Calls", 
            valueFormatter: params => numberFormatter(params.value),
            cellStyle: { textAlign: 'right' }  
        },
        { 
            field: "deal_count", 
            headerName: "Deals", 
            aggFunc: "sum",
            cellStyle: { textAlign: 'right' }  
        },
        {
            field: "calls_to_deals",
            headerName: "Calls To Deals",
            valueGetter: (params) =>
                params.data ? params.data.calls_to_deals : undefined,
            cellStyle: { textAlign: 'right' } 
        },
        {
            field: "avg_duration",
            headerName: "Avg Duration",
            valueGetter: (params) =>
                params.data ? params.data.avg_duration : undefined,
            valueFormatter: params => durationFormatter(params.value),
            cellStyle: { textAlign: 'right' } 
        }
    ];

    // Row Data: the data to be displayed
    const [rowData, setRowData] = useState([]);

    // Preserving Filter Model
    const [filterModel, setFilterModel] = useState(null);

    // Loading state to track when data is being fetched
    const [loading, setLoading] = useState(true);

    // Fetch Data based on date range 
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true); // Set loading to true before fetching
            if (gridRef.current) {
                // Save current filter state
                const currentFilterModel = gridRef.current.api.getFilterModel();
                setFilterModel(currentFilterModel);
            }

            const requestOptions = {
                method: 'POST',
                header: { 'Contect-Type': 'application/json' },
                body: JSON.stringify({
                    replaceKeys: {
                        "{{startDate}}": props.startDate,
                        "{{endDate}}": props.endDate,
                    }
                })
            };
            const reqUrl = `${process.env.REACT_APP_QF_API}/${process.env.REACT_APP_OTG_ID}/data/LyoQkvGPN2HZhhNuime0`;

            try {
                const result = await fetch(reqUrl, requestOptions);
                const respData = await result.json();
                // Check for error 
                if (respData.status !== "success") {
                    alert("Error fetching data: " + respData.msg);
                    setRowData([]);
                    return;
                }
                setRowData(respData.data);
            } catch (error) {
                console.error("ERROR: " + error);
            } finally {
                setLoading(false); // Set loading to false after fetching
            }
        };

        fetchData();
    }, [props.reportName, props.startDate, props.endDate]);

    // After new data is set, reapply the filter model
    useEffect(() => {
        if (gridRef.current && filterModel) {
            gridRef.current.api.setFilterModel(filterModel);
        }
    }, [rowData, filterModel]);

    // This will autosize these columns since I think the reason they're not be resized with the gridoption above
    // is b/c we're calculating them and creating here instead of the data coming straght from the API
    const onGridReady = (params) => {
        params.api.sizeColumnsToFit(); // Resize columns to fit the grid's width
        const columnIds = ['team_name', 'call_date', 'username', 'initial_amount', 'avg_initial', 'calls_to_deals', 'avg_duration', 'deal_count'];
        params.api.autoSizeColumns(columnIds); // Auto-size the specific columns
    };

    return (
        // wrapping container with theme & size
        <div
            className="ag-theme-quartz h-78 greyBg" // applying the grid theme
        >
            <div id="summaryBox">
                <p id="summaryText">
                    <strong>Sales Productivity</strong><br />
                    This view will show a breakdown of performance grouped by team and be able to show a drill down to each sales agent.
                </p>
            </div>
            <div style={{ position: 'relative', height: '85%' }}> {/* New wrapper for AG Grid */}
                <AgGridReact
                    enableCharts={true}
                    enableRangeSelection={true}
                    onGridReady={onGridReady}
                    rowData={rowData}
                    columnDefs={colDefs}
                    getGroupRowAgg={getGroupRowAgg}
                    gridOptions={gridOptions}
                    defaultColDef={defaultColDef}
                    modules={[ServerSideRowModelModule, RowGroupingModule]}
                    defaultSideBar={false}
                    sideBar={{
                        toolPanels: [
                            {
                                id: 'columns',
                                labelDefault: 'Columns',
                                labelKey: 'columns',
                                iconKey: 'columns',
                                toolPanel: 'agColumnsToolPanel',
                            },
                            {
                                id: 'filters',
                                labelDefault: 'Filters',
                                labelKey: 'filters',
                                iconKey: 'filter',
                                toolPanel: 'agFiltersToolPanel',
                            }
                        ]
                    }}
                />
                {loading && (
                    <div className="loading-overlay">
                        <div className="spinner"></div>
                    </div>
                )}
            </div>
        </div>
    )
}